import React, { useState, useEffect } from 'react';
import './InputForm.css';
import { DEFAULT_ACCOUNT_SIZE, DEFAULT_THRESHOLD, getThresholdForAccountSize } from '../utils/accountSizes';
import { sortStrategies } from '../utils/strategySort';
import { resolveStrategyDisplayName } from './dashboard/resolveStrategyDisplayName';
import { PnlContractsInfoButton } from './PnlScalingInfoModal';
import { usePropFirmPresets, findPlan, money, formatVerified, rememberPreset, rememberedPreset } from '../utils/propFirmPresets';

const getDefaultFormData = (riskMode) => ({
  sheetName: '',
  contractType: 'MNQ', // Default to MNQ
  accountSize: DEFAULT_ACCOUNT_SIZE, // Default to 50K for both modes
  contracts: '',
  maxDrawdown: '',
  currentBalance: '', // For apexMae mode
  startOfDayProfit: '',
  profitSinceLastPayout: '', // Optional: profit since last payout for Windfall Rule
  safetyNet: riskMode === 'apexMae' ? DEFAULT_THRESHOLD : '',
});
function InputForm({
  onSubmit,
  loading,
  sheetNames,
  customCombos,
  loadingSheets,
  error,
  riskMode,
  onNavigate,
  initialFormData = null,
  showResetButton = false,
  onReset,
  consistencyPercent = 50,
  onConsistencyPercentChange,
}) {
  const [formData, setFormData] = useState({
    ...getDefaultFormData(riskMode),
    ...(initialFormData || {}),
  });

  useEffect(() => {
    setFormData({
      ...getDefaultFormData(riskMode),
      ...(initialFormData || {}),
    });
  }, [riskMode, initialFormData]);

  // Set default account size and safety net when switching to apexMae mode
  useEffect(() => {
    if (riskMode === 'apexMae') {
      setFormData(prev => ({
        ...prev,
        accountSize: prev.accountSize || DEFAULT_ACCOUNT_SIZE,
        safetyNet: prev.safetyNet || DEFAULT_THRESHOLD
      }));
    }
  }, [riskMode]);

  // Prop firm preset: fills account size, drawdown / safety net and the consistency rule.
  const { firms: presetFirms, live: presetsLive } = usePropFirmPresets();
  const [presetId, setPresetId] = useState(() => rememberedPreset());
  const preset = presetId ? findPlan(presetFirms, presetId) : null;
  const presetSize = preset ? preset.plan.sizes.find((x) => x.accountSize === Number(formData.accountSize)) : null;

  const applySize = (sizeObj) => {
    if (!sizeObj) return;
    setFormData((prev) => ({
      ...prev,
      accountSize: sizeObj.accountSize,
      ...(riskMode === 'risk' ? { maxDrawdown: sizeObj.drawdown } : {}),
      ...(riskMode === 'apexMae' ? { safetyNet: sizeObj.drawdown } : {}),
    }));
  };

  const choosePreset = (id) => {
    setPresetId(id);
    rememberPreset(id);
    const found = id ? findPlan(presetFirms, id) : null;
    if (!found) return;
    const sizes = found.plan.sizes;
    applySize(sizes.find((x) => x.accountSize === Number(formData.accountSize)) || sizes.find((x) => x.accountSize === 50000) || sizes[0]);
    if (riskMode === 'apexMae' && onConsistencyPercentChange && found.plan.consistency) onConsistencyPercentChange(found.plan.consistency);
  };

  // Keep the drawdown in step with a remembered preset once the live list arrives.
  useEffect(() => {
    if (!preset) return;
    const match = preset.plan.sizes.find((x) => x.accountSize === Number(formData.accountSize));
    if (!match) return;
    setFormData((prev) => {
      if (riskMode === 'risk' && prev.maxDrawdown === '') return { ...prev, maxDrawdown: match.drawdown };
      if (riskMode === 'apexMae' && Number(prev.safetyNet) !== match.drawdown) return { ...prev, safetyNet: match.drawdown };
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, presetFirms, riskMode]);

  // Update safety net when account size changes in apexMae mode
  useEffect(() => {
    if (riskMode === 'apexMae' && formData.accountSize) {
      if (presetId) return; // the preset sets it
      const threshold = getThresholdForAccountSize(Number(formData.accountSize));
      if (threshold) {
        setFormData(prev => ({ ...prev, safetyNet: threshold }));
      }
    }
  }, [formData.accountSize, riskMode, presetId]);

  // Calculate start-of-day profit when current balance changes
  useEffect(() => {
    if (riskMode === 'apexMae' && formData.currentBalance && formData.accountSize) {
      const currentBalance = Number(formData.currentBalance);
      const accountSize = Number(formData.accountSize);
      const profit = Math.max(0, currentBalance - accountSize);
      setFormData(prev => ({ ...prev, startOfDayProfit: profit }));
    }
  }, [formData.currentBalance, formData.accountSize, riskMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only send relevant fields based on mode
    const submitData = {
      ...formData,
      consistencyPercent,
      maxDrawdown: riskMode === 'risk' ? formData.maxDrawdown : null,
      startOfDayProfit: riskMode === 'apexMae' ? formData.startOfDayProfit : null,
      safetyNet: riskMode === 'apexMae' ? formData.safetyNet : null,
      profitSinceLastPayout: riskMode === 'apexMae' && formData.profitSinceLastPayout ? formData.profitSinceLastPayout : null,
      // Remove currentBalance from submission (it's only for calculation)
      currentBalance: undefined,
    };
    onSubmit(submitData);
  };

  const handleResetToDefaults = () => {
    setFormData(getDefaultFormData(riskMode));
    setPresetId('');
    rememberPreset('');
    if (onReset) onReset();
  };

  const handleRunSample = (e) => {
    e.preventDefault();
    
    // Always use Sample Strategy (Demo) - it's always available even without Vector membership
    const sampleStrategy = 'Sample Strategy (Demo)';
    
    // Generate random sample data based on mode
    const accountSizes = [25000, 50000, 100000, 150000, 200000];
    const randomAccountSize = accountSizes[Math.floor(Math.random() * accountSizes.length)];
    const randomContracts = Math.floor(Math.random() * 5) + 1; // 1-5 contracts
    
    let sampleData;
    if (riskMode === 'risk') {
      // Risk mode: need maxDrawdown
      const drawdownPercentages = [0.05, 0.06, 0.07, 0.08, 0.10]; // 5-10%
      const randomDrawdown = Math.floor(randomAccountSize * drawdownPercentages[Math.floor(Math.random() * drawdownPercentages.length)]);
      
      sampleData = {
        sheetName: sampleStrategy,
        contractType: Math.random() > 0.5 ? 'MNQ' : 'NQ',
        accountSize: randomAccountSize,
        contracts: randomContracts,
        maxDrawdown: randomDrawdown,
        currentBalance: '',
        startOfDayProfit: '',
        profitSinceLastPayout: '',
        safetyNet: '',
        consistencyPercent,
      };
    } else {
      // % Rule mode: need currentBalance (which calculates startOfDayProfit)
      const profitPercentages = [0.02, 0.03, 0.05, 0.07, 0.10]; // 2-10% profit
      const randomProfit = Math.floor(randomAccountSize * profitPercentages[Math.floor(Math.random() * profitPercentages.length)]);
      const currentBalance = randomAccountSize + randomProfit;
      const threshold = getThresholdForAccountSize(randomAccountSize);
      
      sampleData = {
        sheetName: sampleStrategy,
        contractType: Math.random() > 0.5 ? 'MNQ' : 'NQ',
        accountSize: randomAccountSize,
        contracts: randomContracts,
        maxDrawdown: '',
        currentBalance: currentBalance,
        startOfDayProfit: randomProfit, // Will be recalculated by useEffect
        profitSinceLastPayout: '',
        safetyNet: threshold,
        consistencyPercent,
      };
    }
    
    // Update form data
    setFormData(sampleData);
    
    // Auto-submit after a brief delay to let state update
    setTimeout(() => {
      const submitData = {
        ...sampleData,
        consistencyPercent,
        maxDrawdown: riskMode === 'risk' ? sampleData.maxDrawdown : null,
        startOfDayProfit: riskMode === 'apexMae' ? sampleData.startOfDayProfit : null,
        safetyNet: riskMode === 'apexMae' ? sampleData.safetyNet : null,
        profitSinceLastPayout: null,
        currentBalance: undefined,
      };
      onSubmit(submitData);
    }, 100);
  };

  return (
    <>
      {riskMode === 'apexMae' && (
        <div className="apex-rule-link-container">
          <button 
            className="learn-apex-rule-btn"
            onClick={() => onNavigate && onNavigate('consistency-rules')}
          >
            Learn About Consistency Rules
          </button>
        </div>
      )}
      <div className="input-form-container">
        <form onSubmit={handleSubmit} className={`input-form ${riskMode === 'apexMae' ? 'input-form-apex-mae' : ''}`}>
          <div className="form-section">
            <h2 className="form-title">Strategy Risk Assessment</h2>
            <p className="form-description">
              Select a strategy and configure your trading parameters to assess risk
            </p>
          </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="sheetName" className="form-label">
              Strategy <span className="required">*</span>
            </label>
            <div className="select-wrapper">
              {loadingSheets && <div className="select-spinner"></div>}
              <select
                id="sheetName"
                name="sheetName"
                value={formData.sheetName}
                onChange={handleChange}
                className="form-input form-select"
                required
                disabled={loadingSheets}
              >
                <option value="">{loadingSheets ? 'Loading strategies...' : sheetNames.length === 0 ? 'No strategies found' : 'Select a strategy'}</option>
                {sortStrategies(sheetNames, { preserveOrder: true }).map((name) => (
                  <option key={name} value={name}>
                    {/* Custom Multi-Strat combos use a `custom:<id>` value;
                        render the friendly combo name via the resolver. */}
                    {resolveStrategyDisplayName(name, customCombos)}
                  </option>
                ))}
              </select>
            </div>
            <small className="form-hint">
              {error ? (
                <span style={{ color: '#ef4444' }}>
                  {error}
                </span>
              ) : (
                <>
                  Select the strategy you want to analyze
                </>
              )}
            </small>
          </div>

          {riskMode === 'apexMae' && (
            <div className="form-group">
              <label className="form-label">Consistency Rule <span className="required">*</span></label>
              <select
                className="form-input form-select"
                value={Number(consistencyPercent) || 50}
                onChange={(e) => onConsistencyPercentChange && onConsistencyPercentChange(Number(e.target.value))}
              >
                {[20, 30, 40, 50, 55, 60].map((percent) => (
                  <option key={percent} value={percent}>
                    {percent}%
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Select the consistency rule percentage used for consistency-limit and payout-path calculations.
              </small>
              {onNavigate && (
                <button
                  type="button"
                  className="change-default-consistency-btn"
                  onClick={() => onNavigate('account')}
                >
                  Change default consistency rule
                </button>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Contract Type <span className="required">*</span>
            </label>
            <div className="input-form-contract-type-chips">
              {['NQ', 'MNQ'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`input-form-contract-type-chip ${formData.contractType === type ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, contractType: type }))}
                  aria-label={`Select ${type}`}
                  aria-pressed={formData.contractType === type}
                >
                  <span className="input-form-contract-type-label">
                    {type}
                  </span>
                  <span className="input-form-contract-type-sublabel">
                    {type === 'NQ' ? '(Full-size)' : '(Micro - 1/10th value)'}
                  </span>
                </button>
              ))}
            </div>
            <small className="form-hint">
              Select NQ if sheet data is for full-size contracts, or MNQ if it's for micro contracts
            </small>
          </div>

          <div className="form-group input-form-preset">
            <label htmlFor="propFirmPreset" className="form-label">
              Prop firm preset <span className="optional">(Optional)</span>
            </label>
            <select
              id="propFirmPreset"
              className="form-input form-select"
              value={preset ? presetId : ''}
              onChange={(e) => choosePreset(e.target.value)}
            >
              <option value="">Custom — enter my own numbers</option>
              {presetFirms.map((f) => (
                <optgroup key={f.key} label={f.firm}>
                  {f.plans.map((p) => (
                    <option key={p.id} value={p.id}>{f.firm} · {p.plan}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {preset ? (
              <div className="input-form-preset-card">
                <div className="input-form-preset-facts">
                  <span><b>{preset.plan.drawdownType}</b> drawdown</span>
                  {presetSize && <span>Drawdown <b>{money(presetSize.drawdown)}</b></span>}
                  {presetSize?.dailyLossLimit ? <span>Daily loss limit <b>{money(presetSize.dailyLossLimit)}</b></span> : null}
                  {presetSize?.profitTarget ? <span>Target <b>{money(presetSize.profitTarget)}</b></span> : null}
                  {presetSize?.maxContracts ? <span>Max <b>{presetSize.maxContracts}</b> contracts</span> : null}
                </div>
                <div className="input-form-preset-note">Consistency: {preset.plan.consistencyNote}</div>
                <div className="input-form-preset-meta">
                  <span className={`input-form-preset-badge ${preset.firm.status === 'changed' || preset.firm.confirmed === false ? 'warn' : ''}`}>
                    {preset.firm.confirmed === false ? '⚠ Not yet confirmed against the firm’s pages'
                      : preset.firm.status === 'changed' ? '⚠ Rule change under review' : `✓ Verified ${formatVerified(preset.firm.verifiedAt)}`}
                  </span>
                  {preset.firm.source && (
                    <a href={preset.firm.source} target="_blank" rel="noopener noreferrer nofollow">{preset.firm.firm} rules ↗</a>
                  )}
                  {!presetsLive && <span className="input-form-preset-offline">offline copy</span>}
                </div>
              </div>
            ) : (
              <small className="form-hint">
                Pick your firm and plan to fill in the account size, drawdown and consistency rule from the firm's current rules.
              </small>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Account Size <span className="required">*</span>
            </label>
            {preset ? (
              <div className="input-form-account-size-chips input-form-account-size-chips-wrap">
                {preset.plan.sizes.map((sz) => (
                  <button
                    key={sz.accountSize}
                    type="button"
                    className={`input-form-account-size-chip ${Number(formData.accountSize) === sz.accountSize ? 'active' : ''}`}
                    onClick={() => applySize(sz)}
                  >
                    {`$${sz.label}`}
                  </button>
                ))}
              </div>
            ) : (
            <div className="input-form-account-size-chips-stacked">
              <div className="input-form-account-size-chips">
                {[25000, 50000, 100000].map((presetValue) => (
                  <button
                    key={presetValue}
                    type="button"
                    className={`input-form-account-size-chip ${Number(formData.accountSize || DEFAULT_ACCOUNT_SIZE) === presetValue ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, accountSize: presetValue }))}
                  >
                    {`$${presetValue / 1000}K`}
                  </button>
                ))}
              </div>
              <div className="input-form-account-size-chips">
                {[150000, 250000, 300000].map((presetValue) => (
                  <button
                    key={presetValue}
                    type="button"
                    className={`input-form-account-size-chip ${Number(formData.accountSize || DEFAULT_ACCOUNT_SIZE) === presetValue ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, accountSize: presetValue }))}
                  >
                    {`$${presetValue / 1000}K`}
                  </button>
                ))}
              </div>
            </div>
            )}
            <small className="form-hint">
              {preset
                ? `Sizes ${preset.firm.firm} offers for the ${preset.plan.plan}.`
                : riskMode === 'apexMae'
                  ? 'Select your account size. Without a firm preset, the safety net uses RiskLo’s classic (legacy Apex) thresholds.'
                  : 'Select your original account size (starting capital). Risk percentages are calculated relative to this amount.'}
            </small>
          </div>

          <div className="form-group">
            <div className="input-form-label-with-info-row">
              <label htmlFor="contracts" className="form-label">
                Default Contract Size Multiplier <span className="required">*</span>
              </label>
              <PnlContractsInfoButton iconSize={16} />
            </div>
            <input
              type="number"
              id="contracts"
              name="contracts"
              value={formData.contracts}
              onChange={handleChange}
              placeholder="10"
              className="form-input"
              required
              min="1"
              step="1"
            />
          </div>

          {riskMode === 'risk' && (
            <div className="form-group">
              <label htmlFor="maxDrawdown" className="form-label">
                Max Trailing Drawdown ($) <span className="required">*</span>
              </label>
              <input
                type="number"
                id="maxDrawdown"
                name="maxDrawdown"
                value={formData.maxDrawdown}
                onChange={handleChange}
                placeholder="2500"
                className="form-input"
                required
                min="1"
                step="0.01"
              />
              <small className="form-hint">
                {preset && presetSize
                  ? `Set from ${preset.firm.firm} ${preset.plan.plan} (${presetSize.label}). You can change it.`
                  : 'Your account\'s maximum trailing drawdown limit (from your prop firm or NinjaTrader)'}
              </small>
            </div>
          )}

          {riskMode === 'apexMae' && (
            <>
              <div className="form-group">
                <label htmlFor="currentBalance" className="form-label">
                  Acct. Balance ($) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="currentBalance"
                  name="currentBalance"
                  value={formData.currentBalance}
                  onChange={handleChange}
                  placeholder="52500"
                  className="form-input"
                  required={riskMode === 'apexMae'}
                  min="0"
                  step="0.01"
                />
                <small className="form-hint">
                  Your current account balance (e.g., if you started with $50k and now have $52,500, enter 52500)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="startOfDayProfit" className="form-label">
                  Start-of-Day Profit Balance ($)
                </label>
                <input
                  type="number"
                  id="startOfDayProfit"
                  name="startOfDayProfit"
                  value={formData.startOfDayProfit}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                />
                <small className="form-hint">
                  Automatically calculated: Current Balance - Account Size = ${formData.startOfDayProfit || '0.00'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="safetyNet" className="form-label">
                  Trailing Threshold / Safety Net ($)
                </label>
                <input
                  type="number"
                  id="safetyNet"
                  name="safetyNet"
                  value={formData.safetyNet}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                />
                <small className="form-hint">
                  {preset
                    ? `Set from the ${preset.firm.firm} ${preset.plan.plan} drawdown ($${Number(formData.safetyNet || 0).toLocaleString()} for $${Number(formData.accountSize || 0).toLocaleString()}).`
                    : <>Automatically set based on your account size (${formData.safetyNet || '0.00'} for {formData.accountSize ? `$${Number(formData.accountSize).toLocaleString()}` : 'selected account'})</>}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="profitSinceLastPayout" className="form-label">
                  Profit Since Last Payout ($) <span className="optional">(Optional)</span>
                </label>
                <input
                  type="number"
                  id="profitSinceLastPayout"
                  name="profitSinceLastPayout"
                  value={formData.profitSinceLastPayout}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Leave empty to use start-of-day profit"
                  step="0.01"
                  min="0"
                />
                <small className="form-hint">
                  For more accurate Windfall Rule calculation, enter your profit accumulated since your last approved payout. 
                  If left empty, RiskLo will use your start-of-day profit balance.
                </small>
              </div>
            </>
          )}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Analyzing...' : 'Analyze Risk'}
        </button>
        
        <button
          type="button"
          className="sample-button"
          onClick={handleRunSample}
          disabled={loading || loadingSheets}
        >
          🎲 Run Sample Analysis
        </button>
        {showResetButton && (
          <button
            type="button"
            className="reset-button"
            onClick={handleResetToDefaults}
            disabled={loading}
          >
            Reset to defaults
          </button>
        )}
      </form>
      </div>
    </>
  );
}

export default InputForm;

